/**
 * Field-mapping service for SubContractorHub (Story SCH-2.1).
 *
 * Wraps the five Field Mapping CRUD endpoints + the discover-catalog
 * endpoint. Mirrors the snake_case → camelCase translation pattern from
 * subcontractorHubService for envelope handling.
 */

import { supabase } from '@/lib/supabaseClient';
import type {
  AutoMatchResult,
  BulkCreateFieldMappingsRequest,
  BulkCreateFieldMappingsResult,
  CreateFieldMappingErrorCode,
  CreateFieldMappingErrorObject,
  CreateFieldMappingRequest,
  CreateFieldMappingResult,
  DeleteFieldMappingResult,
  DiscoverCatalogErrorCode,
  DiscoverCatalogResponse,
  DiscoverCatalogResult,
  FieldMappingRow,
  JoulesChecklistCandidate,
  ReorderFieldMappingsRequest,
  ReorderFieldMappingsResult,
  SchCustomField,
  UpdateFieldMappingRequest,
  UpdateFieldMappingResult,
} from '@/types/integrations/fieldMapping';

const BACKEND_URL =
  import.meta.env.VITE_PYTHON_BACKEND_URL || 'http://localhost:8000';

async function authHeader(): Promise<HeadersInit> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }
  return {
    Authorization: `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
  };
}

interface ApiEnvelope<TData> {
  data: TData | null;
  error: { code: string; message: string; details?: Record<string, unknown> } | null;
  meta: unknown;
}

async function fetchJson<TData>(
  path: string,
  init: RequestInit,
): Promise<ApiEnvelope<TData>> {
  const headers = await authHeader();
  const response = await fetch(`${BACKEND_URL}${path}`, {
    ...init,
    headers: { ...headers, ...(init.headers ?? {}) },
  });
  if (!response.ok && response.status !== 200) {
    let detail: string | undefined;
    try {
      const json = await response.json();
      detail = typeof json?.detail === 'string' ? json.detail : undefined;
    } catch {
      // ignore parse error
    }
    throw new Error(detail || `Request failed with status ${response.status}`);
  }
  return (await response.json()) as ApiEnvelope<TData>;
}

// ─── snake_case → camelCase row mappers ───────────────────────────────────
interface RawSchCustomField {
  field_key: string;
  field_label: string;
  field_type: string;
  options: string[] | null;
}

interface RawJoulesCandidate {
  id: number;
  // The backend serializes via the 'item_name' alias; Pydantic v2 dumps
  // the Python field name (`label`) by default. We accept both shapes here
  // because the discover-catalog endpoint calls model_dump(by_alias=True),
  // which keeps the alias on output.
  item_name?: string;
  label?: string;
  item_type: string;
  template_id: number | null;
  template_name: string | null;
}

interface RawAutoMatchResult {
  joules_target_id: number;
  confidence: number;
}

interface RawFieldMappingRow {
  id: string;
  tenant_integration_id: string;
  external_field_key: string;
  external_field_label: string;
  external_field_type: string;
  joules_target_type: 'checklist_item' | 'project_column' | 'skip';
  joules_target_id: number | null;
  joules_target_column: string | null;
  enabled: boolean;
  precedence: number;
  write_policy: 'always_overwrite' | 'overwrite_unless_modified' | 'never_overwrite';
  display_format: string | null;
  notes: string | null;
  is_dormant: boolean;
  created_at: string | null;
  updated_at: string | null;
}

interface RawDiscoverCatalogData {
  sch_fields: RawSchCustomField[];
  joules_candidates: RawJoulesCandidate[];
  suggestions: Record<string, RawAutoMatchResult | null>;
  existing_mappings: RawFieldMappingRow[];
  schema_fingerprint: string;
  schema_drift_detected: boolean;
}

function _mapSchField(raw: RawSchCustomField): SchCustomField {
  return {
    fieldKey: raw.field_key,
    fieldLabel: raw.field_label,
    fieldType: raw.field_type,
    options: raw.options ?? null,
  };
}

function _mapCandidate(raw: RawJoulesCandidate): JoulesChecklistCandidate {
  return {
    id: raw.id,
    label: raw.item_name ?? raw.label ?? '',
    itemType: raw.item_type,
    templateId: raw.template_id,
    templateName: raw.template_name ?? null,
  };
}

function _mapAutoMatch(raw: RawAutoMatchResult): AutoMatchResult {
  return {
    joulesTargetId: raw.joules_target_id,
    confidence: raw.confidence,
  };
}

function _mapMappingRow(raw: RawFieldMappingRow): FieldMappingRow {
  return {
    id: raw.id,
    tenantIntegrationId: raw.tenant_integration_id,
    externalFieldKey: raw.external_field_key,
    externalFieldLabel: raw.external_field_label,
    externalFieldType: raw.external_field_type,
    joulesTargetType: raw.joules_target_type,
    joulesTargetId: raw.joules_target_id,
    joulesTargetColumn: raw.joules_target_column,
    enabled: raw.enabled,
    precedence: raw.precedence,
    writePolicy: raw.write_policy,
    displayFormat: raw.display_format,
    notes: raw.notes,
    isDormant: raw.is_dormant,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

function _mapDiscoverCatalog(raw: RawDiscoverCatalogData): DiscoverCatalogResponse {
  const suggestions: Record<string, AutoMatchResult | null> = {};
  for (const [key, value] of Object.entries(raw.suggestions ?? {})) {
    suggestions[key] = value ? _mapAutoMatch(value) : null;
  }
  return {
    schFields: (raw.sch_fields ?? []).map(_mapSchField),
    joulesCandidates: (raw.joules_candidates ?? []).map(_mapCandidate),
    suggestions,
    existingMappings: (raw.existing_mappings ?? []).map(_mapMappingRow),
    schemaFingerprint: raw.schema_fingerprint,
    schemaDriftDetected: !!raw.schema_drift_detected,
  };
}

function _mapCreatePayload(req: CreateFieldMappingRequest): Record<string, unknown> {
  // Build request body in snake_case. Omit undefined fields (so backend's
  // pydantic model uses defaults for write_policy etc.).
  const out: Record<string, unknown> = {
    external_field_key: req.externalFieldKey,
    external_field_label: req.externalFieldLabel,
    external_field_type: req.externalFieldType,
    joules_target_type: req.joulesTargetType,
  };
  if (req.joulesTargetId !== undefined) out.joules_target_id = req.joulesTargetId;
  if (req.joulesTargetColumn !== undefined) out.joules_target_column = req.joulesTargetColumn;
  if (req.writePolicy !== undefined) out.write_policy = req.writePolicy;
  if (req.displayFormat !== undefined) out.display_format = req.displayFormat;
  if (req.notes !== undefined) out.notes = req.notes;
  return out;
}

function _normalizeError(
  raw: { code: string; message: string; details?: Record<string, unknown> } | null,
  knownCodes: readonly CreateFieldMappingErrorCode[],
): CreateFieldMappingErrorObject {
  if (!raw) {
    return { code: 'internal_error', message: 'Unknown error' };
  }
  const code = (knownCodes as readonly string[]).includes(raw.code)
    ? (raw.code as CreateFieldMappingErrorCode)
    : 'internal_error';
  return { code, message: raw.message, details: raw.details };
}

const CRUD_ERROR_CODES = [
  'integration_not_found',
  'mapping_conflict',
  'internal_error',
] as const;

export const fieldMappingService = {
  // ─── GET /discover-catalog ──────────────────────────────────────────────
  async discoverCatalog(): Promise<DiscoverCatalogResult> {
    const env = await fetchJson<RawDiscoverCatalogData>(
      '/api/v1/integrations/subcontractorhub/discover-catalog',
      { method: 'GET' },
    );
    if (env.data) {
      return { data: _mapDiscoverCatalog(env.data), error: null };
    }
    if (env.error) {
      const knownCodes: readonly DiscoverCatalogErrorCode[] = [
        'integration_not_found',
        'auth_failed',
        'connection_test_failed',
        'internal_error',
      ];
      const code = (knownCodes as readonly string[]).includes(env.error.code)
        ? (env.error.code as DiscoverCatalogErrorCode)
        : 'internal_error';
      return {
        data: null,
        error: { code, message: env.error.message, details: env.error.details },
      };
    }
    return {
      data: null,
      error: { code: 'internal_error', message: 'Unknown error' },
    };
  },

  // ─── POST /mappings/field (single UPSERT) ──────────────────────────────
  async createFieldMapping(
    req: CreateFieldMappingRequest,
  ): Promise<CreateFieldMappingResult> {
    const env = await fetchJson<RawFieldMappingRow>(
      '/api/v1/integrations/subcontractorhub/mappings/field',
      { method: 'POST', body: JSON.stringify(_mapCreatePayload(req)) },
    );
    if (env.data) {
      return { data: _mapMappingRow(env.data), error: null };
    }
    return { data: null, error: _normalizeError(env.error, CRUD_ERROR_CODES) };
  },

  // ─── POST /mappings/field/bulk (atomic INSERT) ─────────────────────────
  async bulkCreateFieldMappings(
    req: BulkCreateFieldMappingsRequest,
  ): Promise<BulkCreateFieldMappingsResult> {
    const env = await fetchJson<{ rows: RawFieldMappingRow[] }>(
      '/api/v1/integrations/subcontractorhub/mappings/field/bulk',
      {
        method: 'POST',
        body: JSON.stringify({ rows: req.rows.map(_mapCreatePayload) }),
      },
    );
    if (env.data) {
      return { data: { rows: env.data.rows.map(_mapMappingRow) }, error: null };
    }
    return { data: null, error: _normalizeError(env.error, CRUD_ERROR_CODES) };
  },

  // ─── PATCH /mappings/field/reorder ─────────────────────────────────────
  async reorderFieldMappings(
    req: ReorderFieldMappingsRequest,
  ): Promise<ReorderFieldMappingsResult> {
    const env = await fetchJson<{ updated: number }>(
      '/api/v1/integrations/subcontractorhub/mappings/field/reorder',
      {
        method: 'PATCH',
        body: JSON.stringify({
          ordering: req.ordering.map((e) => ({ id: e.id, precedence: e.precedence })),
        }),
      },
    );
    if (env.data) {
      return { data: { updated: env.data.updated }, error: null };
    }
    return { data: null, error: _normalizeError(env.error, CRUD_ERROR_CODES) };
  },

  // ─── PATCH /mappings/field/{id} ────────────────────────────────────────
  async updateFieldMapping(
    id: string,
    req: UpdateFieldMappingRequest,
  ): Promise<UpdateFieldMappingResult> {
    const body: Record<string, unknown> = {};
    if (req.joulesTargetType !== undefined) body.joules_target_type = req.joulesTargetType;
    if (req.joulesTargetId !== undefined) body.joules_target_id = req.joulesTargetId;
    if (req.joulesTargetColumn !== undefined) body.joules_target_column = req.joulesTargetColumn;
    if (req.enabled !== undefined) body.enabled = req.enabled;
    if (req.precedence !== undefined) body.precedence = req.precedence;
    if (req.writePolicy !== undefined) body.write_policy = req.writePolicy;
    if (req.displayFormat !== undefined) body.display_format = req.displayFormat;
    if (req.notes !== undefined) body.notes = req.notes;
    if (req.isDormant !== undefined) body.is_dormant = req.isDormant;

    const env = await fetchJson<RawFieldMappingRow>(
      `/api/v1/integrations/subcontractorhub/mappings/field/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    );
    if (env.data) {
      return { data: _mapMappingRow(env.data), error: null };
    }
    return { data: null, error: _normalizeError(env.error, CRUD_ERROR_CODES) };
  },

  // ─── DELETE /mappings/field/{id} ───────────────────────────────────────
  async deleteFieldMapping(id: string): Promise<DeleteFieldMappingResult> {
    const env = await fetchJson<{ deleted_id: string }>(
      `/api/v1/integrations/subcontractorhub/mappings/field/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    );
    if (env.data) {
      return { data: { deletedId: env.data.deleted_id }, error: null };
    }
    return { data: null, error: _normalizeError(env.error, CRUD_ERROR_CODES) };
  },
};
